import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { ReactElement } from 'react'
import { api } from '../../api'
import { RequireSession, useSitzung } from '../../auth'
import { Betrag } from '../../components/Betrag'
import { Knopf } from '../../components/Knopf'
import { Tabelle } from '../../components/Tabelle'
import { TabellenZustand, Zustand } from '../../components/Zustand'
import { datum } from '../../format'

/** Eine Zeile aus `myCommissions` (src/worker/services/reads.ts, MyCommissionRow). */
export interface MeineProvision {
  id: string
  tenant_id: string
  tenant_name: string
  ref_no: number
  recipient_tc_id: string | null
  empfaenger: string
  role_label: string
  leistung_text: string
  rate_nr: number | null
  amount_cents: number
  kind: string
  status: string
  customer_name: string | null
  product_code: string | null
  effective_date: string | null
  paid_at: string | null
  contractor_invoice_id: string | null
  created_at: string
  /** 1: offen, aber vom Auftraggeber noch nicht bestätigt — noch nicht abrechenbar */
  wartet_auf_bestaetigung?: number
}

/**
 * Der Saldo über alle Auftraggeber und alle Jahre, gerechnet in derselben Datenbank, in der die
 * nächtliche Prüfung C2 ihn kontrolliert. Er ist vom Query-Filter unberührt — deshalb wird er
 * ausschließlich aus der ungefilterten Abfrage gelesen und hier **nie** nachgerechnet: eine zweite
 * Implementierung derselben Aufteilung wäre still falsch, sobald die Liste bei `LIST_CAP = 5000`
 * abgeschnitten wird.
 *
 * Der Server liefert das Feld aus `myBalance` (services/reads.ts) — ohne den Filter zu kennen. Der
 * Saldo gehört dem Auftragnehmer, nicht seiner gerade eingestellten Ansicht: einer, der sich beim
 * Umstellen eines Filters bewegt, ist kein Saldo.
 */
export interface MeinSaldo {
  faellig_cents: number
  ausgezahlt_cents: number
  offen_cents: number
  /** jetzt in Rechnung stellbar: bestätigt oder ohne Bestätigungspflicht, Korrekturen eingeschlossen */
  auszahlbar_cents?: number
}

export interface ProvisionenAntwort {
  provisionen: MeineProvision[]
  saldo: MeinSaldo
}

/** Die sieben Zustände aus `CommissionStatus`; `voided` liefert dieser Endpunkt nie. */
export const PROVISION_STATUS: Readonly<Record<string, string>> = {
  open: 'Offen',
  in_draft: 'Im Entwurf',
  submitted: 'Eingereicht',
  approved: 'Freigegeben',
  paid: 'Ausgezahlt',
  voided: 'Storniert',
  settled_externally: 'Extern ausgeglichen',
}

export function statusLabel(status: string): string {
  return PROVISION_STATUS[status] ?? status
}

/**
 * Die Auswahl des Statusfilters: der feste Katalog aus `migrations/0001_init.sql` (CHECK auf
 * `commissions.status`) ohne `voided`, das dieser Endpunkt nie liefert. Bewusst **nicht** aus den
 * geladenen Zeilen abgeleitet — sobald die Liste bei `LIST_CAP = 5000` abgeschnitten wird, fehlte im
 * Filter genau der Status, dessen Zeilen fehlen, und er wäre damit gar nicht mehr erreichbar.
 */
export const STATUS_FILTER: readonly string[] = [
  'open',
  'in_draft',
  'submitted',
  'approved',
  'paid',
  'settled_externally',
]

export interface ProvisionenSuche {
  tenant?: string
  status?: string
}

export function provisionenPfad(suche: ProvisionenSuche): string {
  const parameter = new URLSearchParams()
  if (suche.tenant !== undefined && suche.tenant !== '') parameter.set('tenant', suche.tenant)
  if (suche.status !== undefined && suche.status !== '') parameter.set('status', suche.status)
  const abfrage = parameter.toString()
  return abfrage === '' ? '/api/me/provisionen' : `/api/me/provisionen?${abfrage}`
}

/** Das Abrechnungsziel für genau einen Empfänger (Task 85 legt den Bildschirm an). */
export function abrechnenPfad(tenantContractorId: string): string {
  return `/me/abrechnen?${new URLSearchParams({ tc: tenantContractorId, alle: '1' }).toString()}`
}

function text(wert: string | null): string {
  return wert === null || wert === '' ? '—' : wert
}

/**
 * Die Spalten der Kopfzeile weiter unten: Kunde, Produkt, Rolle, Rate, Betrag, Status, Bezahlt am.
 * Bei mehreren Profilen kommt „Empfänger" davor. Das Skelett und die Zustandszeile müssen dieselbe
 * Breite haben wie der Kopf — wird dort eine Spalte ergänzt, gehört sie hier dazu.
 */
const SPALTEN_BASIS = 7
const SPALTE_EMPFAENGER = 1

export function ProvisionenScreen({
  suche,
  onSuche,
  mehrereEmpfaenger,
}: {
  suche: ProvisionenSuche
  onSuche: (suche: ProvisionenSuche) => void
  mehrereEmpfaenger: boolean
}): ReactElement {
  // Zwei Abfragen, mit Absicht: die gefilterte füllt die Tabelle, die ungefilterte trägt Saldo,
  // Filterauswahl und die Abrechnungsziele. Ein Filter darf den Saldo nicht bewegen — sonst zeigt
  // der Bildschirm je nach Auswahl eine andere Summe, und das ist bei eigenem Geld das Ende des
  // Vertrauens.
  //
  // Beide Schlüssel tragen den Pfad, den sie wirklich anfragen. Ohne Filter ist das derselbe Pfad,
  // und damit fallen die beiden Abfragen im Cache auf einen Eintrag und eine einzige Anfrage
  // zusammen — sonst holte der Regelfall dieselbe (bis zu 5000 Zeilen lange) Liste zweimal.
  const pfad = provisionenPfad(suche)
  const eineAbfrage = pfad === '/api/me/provisionen'
  const alle = useQuery({
    queryKey: ['me', 'provisionen', '/api/me/provisionen'],
    queryFn: () => api.get<ProvisionenAntwort>('/api/me/provisionen'),
  })
  const gefiltert = useQuery({
    queryKey: ['me', 'provisionen', pfad],
    queryFn: () => api.get<ProvisionenAntwort>(pfad),
  })

  const alleZeilen = alle.data?.provisionen ?? []
  const zeilen = gefiltert.data?.provisionen ?? []
  const saldo = alle.data?.saldo

  // Zwei Abfragen, zwei Zustände — mit Absicht getrennt und nicht zu einem verodert. Sie tragen
  // verschiedene Schlüssel und treffen deshalb zu verschiedenen Zeiten ein; ein gemeinsamer Zustand
  // ließe entweder das Skelett über echten Zeilen stehen oder eine Fehlermeldung unter Daten, die
  // längst geladen sind. Also: die Tabelle unten hängt allein an `gefiltert`, und ein Scheitern von
  // `alle` steht dort, wo dessen Daten stünden — über dem Filter, bei Saldo und Abrechnungszielen.
  // Ohne Filter ist beides ein und dieselbe Abfrage; dann sagt die Tabelle es bereits.

  const spalten = SPALTEN_BASIS + (mehrereEmpfaenger ? SPALTE_EMPFAENGER : 0)

  // Anders als beim Status gibt es für die Auftraggeber keine truncierungsfeste Quelle: die Sitzung
  // führt unter `tenants` die Auftraggeber, bei denen man **Administrator** ist, und unter
  // `contractors` die eigenen Profile — die Auftraggeber, für die man als Auftragnehmer arbeitet,
  // stehen in keiner von beiden. Also bleiben die geladenen Zeilen die einzige Quelle, mit der
  // bekannten Folge: jenseits von `LIST_CAP = 5000` fehlt im Filter, wessen Zeilen fehlen. Der
  // Ausweg ist eine Server-Liste, nicht eine zweite Ableitung hier.
  const auftraggeber = [...new Map(alleZeilen.map((zeile) => [zeile.tenant_id, zeile.tenant_name])).entries()]

  // Ein Abrechnungsziel je Empfängerzeile, die offene Provisionen hat (Spec §1.10 Schritt 1).
  const abrechenbar = [
    ...new Map(
      alleZeilen.flatMap((zeile): [string, MeineProvision][] =>
        zeile.status === 'open' && zeile.recipient_tc_id !== null && zeile.wartet_auf_bestaetigung !== 1
          ? [[zeile.recipient_tc_id, zeile]] : [],
      ),
    ).entries(),
  ]

  return (
    <div className="seite">
      <h1>Meine Provisionen</h1>

      {/* Scheitert die ungefilterte Abfrage, fehlen Saldo, Abrechnungsziele und die Auswahl der
          Auftraggeber. Das steht hier, an ihrer Stelle — sonst verschwänden sie wortlos, während die
          Tabelle darunter unbeirrt Zeilen zeigt. Ohne Filter sind beide Abfragen dieselbe; dann
          steht die Meldung schon in der Tabelle und würde hier nur doppelt. */}
      {alle.isError && !eineAbfrage && (
        <Zustand art="fehler" fehler={alle.error} onWiederholen={() => void alle.refetch()} />
      )}

      {/* Kein Saldo ohne Zahl vom Server: eine 0 aus einer fehlgeschlagenen oder unvollständigen
          Antwort wäre eine Falschaussage. Fehlt das Feld, sagt der Bildschirm das — er lässt die
          Stelle nicht wortlos leer. */}
      {saldo !== undefined && (
        <div className="saldo-karte">
          <span id="saldo-beschriftung" className="saldo-beschriftung">
            Offener Saldo
          </span>
          <p
            aria-labelledby="saldo-beschriftung"
            className="zahl-gross mt-1"
            style={{ fontSize: 'clamp(2.75rem, 2rem + 3vw, 4.25rem)' }}
          >
            <Betrag cents={saldo.offen_cents} />
          </p>
          {saldo.auszahlbar_cents === undefined ? null : (
            <p className="saldo-auszahlbar">
              Davon jetzt auszahlbar: <strong><Betrag cents={saldo.auszahlbar_cents} /></strong>
            </p>
          )}
          <p className="saldo-erklaerung">
            Alles, was noch nicht ausgezahlt ist — über alle Auftraggeber hinweg und unabhängig vom Filter
            unten. Die Zahl kommt aus derselben Aufteilung, mit der die nächtliche Prüfung die Salden
            kontrolliert; hier wird nichts nachgerechnet.
          </p>
        </div>
      )}

      {abrechenbar.length > 0 && (
        <p className="saldo-ziele">
          {/* Ein gewöhnliches <a>, kein <Link>: /me/abrechnen legt erst Task 85 an, und der erzeugte
              Routenbaum kennt das Ziel bis dahin nicht. */}
          {abrechenbar.map(([tcId, zeile]) => (
            <a
              key={tcId}
              className="knopf"
              data-variante="primaer"
              href={abrechnenPfad(tcId)}
            >
              Rechnung erstellen — {zeile.tenant_name}
              {mehrereEmpfaenger ? ` · ${zeile.empfaenger}` : ''}
            </a>
          ))}
        </p>
      )}

      <div className="leiste mb-4">
        <span className="feld">
          <label htmlFor="filter-auftraggeber">Auftraggeber</label>
          <select
            id="filter-auftraggeber"
            value={suche.tenant ?? ''}
            onChange={(ereignis) =>
              onSuche({ ...suche, tenant: ereignis.target.value === '' ? undefined : ereignis.target.value })
            }
          >
            <option value="">Alle</option>
            {auftraggeber.map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </span>
        <span className="feld">
          <label htmlFor="filter-status">Status</label>
          <select
            id="filter-status"
            value={suche.status ?? ''}
            onChange={(ereignis) =>
              onSuche({ ...suche, status: ereignis.target.value === '' ? undefined : ereignis.target.value })
            }
          >
            <option value="">Alle</option>
            {STATUS_FILTER.map((wert) => (
              <option key={wert} value={wert}>{statusLabel(wert)}</option>
            ))}
          </select>
        </span>
        <Knopf variante="still" onClick={() => onSuche({})}>Filter zurücksetzen</Knopf>
      </div>

      <Tabelle
        beschriftung="Meine Provisionen"
        kopf={
          <tr>
            {mehrereEmpfaenger && <th scope="col">Empfänger</th>}
            <th scope="col">Kunde</th>
            <th scope="col">Produkt</th>
            <th scope="col">Rolle</th>
            <th scope="col">Rate</th>
            <th scope="col" className="zahl">Betrag</th>
            <th scope="col">Status</th>
            <th scope="col">Bezahlt am</th>
          </tr>
        }
      >
        {gefiltert.isPending && (
          <TabellenZustand art="laedt" spalten={spalten} beschriftung="Ihre Provisionen werden geladen." />
        )}
        {gefiltert.isError && (
          <TabellenZustand
            art="fehler"
            spalten={spalten}
            fehler={gefiltert.error}
            onWiederholen={() => void gefiltert.refetch()}
          />
        )}
        {gefiltert.isSuccess && zeilen.length === 0 && (
          <TabellenZustand
            art="leer"
            spalten={spalten}
            titel="Noch keine Provisionen"
            hinweis="Sobald eine Zahlung Ihres Auftraggebers eintrifft und einem Abschluss zugeordnet ist, erscheint hier eine Zeile."
          />
        )}
        {gefiltert.isSuccess && zeilen.map((zeile) => (
          <tr key={zeile.id}>
            {mehrereEmpfaenger && <td>{zeile.empfaenger}</td>}
            <td>{text(zeile.customer_name)}</td>
            <td>{text(zeile.product_code)}</td>
            <td>{zeile.role_label}</td>
            <td>{zeile.rate_nr === null ? '—' : `Rate ${zeile.rate_nr}`}</td>
            <td className="zahl"><Betrag cents={zeile.amount_cents} /></td>
            <td>{(zeile.wartet_auf_bestaetigung === 1 ? 'Offen — wartet auf Bestätigung' : statusLabel(zeile.status))}</td>
            <td>{zeile.paid_at === null ? '—' : datum(zeile.paid_at)}</td>
          </tr>
        ))}
      </Tabelle>

      {/* Die Fußnote erklärt eine Spalte, die nur mit Daten etwas anzeigt — sie steht deshalb nur
          bei gefüllter Tabelle und nicht unter dem Lade-, Fehler- oder Leerzustand. */}
      {zeilen.length > 0 && (
        <p className="mt-4 max-w-prose text-sm text-[var(--ink-muted)]">
          „Bezahlt am“ ist der Zahlungseingang des Kunden — das Datum, an dem Ihre Provision entstanden ist.
          Wann sie an Sie überwiesen wurde, steht unter „Meine Abrechnungen“.
        </p>
      )}
    </div>
  )
}

/**
 * Der Inhalt hinter dem Zugangsschutz. `useSitzung()` ist nur innerhalb von <RequireSession>
 * zulässig (src/client/auth.tsx) — es wirft, solange die Sitzungsabfrage läuft oder fehlgeschlagen
 * ist. Deshalb steht der Wächter außen und diese Komponente innen: erst wenn er die Sitzung
 * durchgelassen hat, wird hier gelesen.
 */
function ProvisionenInhalt(): ReactElement {
  const suche = Route.useSearch()
  const navigate = useNavigate()
  const { contractors } = useSitzung()
  return (
    <ProvisionenScreen
      suche={suche}
      mehrereEmpfaenger={contractors.length > 1}
      onSuche={(neu) => void navigate({ to: '/me/provisionen', search: neu })}
    />
  )
}

function ProvisionenRoute(): ReactElement {
  return (
    <RequireSession>
      <ProvisionenInhalt />
    </RequireSession>
  )
}

export const Route = createFileRoute('/me/provisionen')({
  validateSearch: (roh: Record<string, unknown>): ProvisionenSuche => ({
    tenant: typeof roh['tenant'] === 'string' && roh['tenant'] !== '' ? roh['tenant'] : undefined,
    status: typeof roh['status'] === 'string' && roh['status'] !== '' ? roh['status'] : undefined,
  }),
  component: ProvisionenRoute,
})
